import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PaymentProvider, RolloverType } from '@rollops/app-shared/types/billing';

export type OnboardingSection = 'company' | 'locations' | 'billing' | 'subscriptions' | 'waiver' | 'complete';
export type CompanyStep = 'business-details' | 'contact-info' | 'address' | 'branding' | 'review';
export type BillingStep = 
  | 'billing-date' 
  | 'late-fees' 
  | 'class-age-adherence'
  | 'has-payment-provider'
  | 'migration-strategy'
  | 'fee-structure'
  | 'stripe-setup'
  | 'complete';

export interface CompanyData {
  name: string;
  slogan: string;
  ein: string;
  contactFirstName: string;
  contactLastName: string;
  email: string;
  phone: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  place_id?: string;
  business_name?: string;
  logoUrl: string;
}

interface BillingData {
  // Provider selection
  hasPaymentProvider: boolean | null; // Store the answer to "Do you already have a payment provider?"
  provider: PaymentProvider | null; // Only two types: 'rollops' | 'self-managed'
  rolloverType: RolloverType | null; // Configuration option, not provider type
  
  // Common settings
  billingStrategy: 'monthly_fixed_date' | 'on_signup' | null;
  fixedBillingDay: number | null;
  lateFeeStrategy: 'staged' | 'max_total' | null;
  lateFeeConfig: { days: number; amount: number }[] | { max_amount: number; days_until_cancellation: number } | null;
  cancellationNoticeDays: number;
  classAgeAdherencePolicy: { policy: 'open' | 'request' | 'strict' } | null;
  
  // RollOps-specific
  feeScheduleId: string | null;
  stripeConnected: boolean;

  // Rollover-specific (rollover-self-managed)
  rolloverBillingModel: 'monthly_fixed_date' | 'on_signup' | null;
  rolloverPriceOption: 'same_price' | 'increased_price' | null;
  rolloverSubscriptionSelection: 'user_selects' | 'invite_specific' | null;

  additionalTerms: string;
}

interface OnboardingStore {
  // Company data
  companyId: string | null;
  companyData: CompanyData;
  
  // Billing data
  billingData: BillingData;
  
  // Navigation state
  currentSection: OnboardingSection;
  currentCompanyStep: CompanyStep;
  currentBillingStep: BillingStep;
  billingSectionVisited: boolean; // Track if user visited billing section
  
  // Actions
  setCompanyId: (id: string | null) => void;
  setCompanyData: (data: Partial<CompanyData>) => void;
  setBillingData: (data: Partial<BillingData>) => void;
  setCurrentSection: (section: OnboardingSection) => void;
  setCurrentCompanyStep: (step: CompanyStep) => void;
  setCurrentBillingStep: (step: BillingStep) => void;
  setBillingSectionVisited: (visited: boolean) => void;
  resetOnboarding: () => void;
}

const initialCompanyData: CompanyData = {
  name: '',
  slogan: '',
  ein: '',
  contactFirstName: '',
  contactLastName: '',
  email: '',
  phone: '',
  street: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  logoUrl: '',
};

const initialBillingData: BillingData = {
  hasPaymentProvider: null,
  provider: 'rollops', // Default to RollOps Billing (recommended)
  rolloverType: null,
  billingStrategy: 'monthly_fixed_date',
  fixedBillingDay: 1,
  lateFeeStrategy: 'staged',
  lateFeeConfig: [
    { days: 5, amount: 5 },
    { days: 10, amount: 10 },
    { days: 15, amount: 15 },
  ],
  cancellationNoticeDays: 30,
  classAgeAdherencePolicy: null,
  feeScheduleId: null,
  stripeConnected: false,
  rolloverBillingModel: null,
  rolloverPriceOption: null,
  rolloverSubscriptionSelection: null,
  additionalTerms: '',
};

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set) => ({
      companyId: null,
      companyData: initialCompanyData,
      billingData: initialBillingData,
      currentSection: 'company',
      currentCompanyStep: 'business-details',
      currentBillingStep: 'has-payment-provider',
      billingSectionVisited: false,

      setCompanyId: (id) => set({ companyId: id }),
      
      setCompanyData: (data) =>
        set((state) => ({
          companyData: { ...state.companyData, ...data },
        })),
      
      setBillingData: (data) =>
        set((state) => ({
          billingData: { ...state.billingData, ...data },
        })),
      
      setCurrentSection: (section) => 
        set((state) => {
          // Track if billing section was visited
          const billingVisited = section === 'billing' ? true : state.billingSectionVisited;
          return { 
            currentSection: section,
            billingSectionVisited: billingVisited,
          };
        }),
      
      setCurrentCompanyStep: (step) => set({ currentCompanyStep: step }),
      
      setCurrentBillingStep: (step) => set({ currentBillingStep: step }),
      
      setBillingSectionVisited: (visited) => set({ billingSectionVisited: visited }),
      
      resetOnboarding: () =>
        set({
          companyId: null,
          companyData: initialCompanyData,
          billingData: initialBillingData,
          currentSection: 'company',
          currentCompanyStep: 'business-details',
          currentBillingStep: 'has-payment-provider', // Match initial state
          billingSectionVisited: false,
        }),
    }),
    {
      name: 'rollops-onboarding',
    }
  )
);

